// src/hooks/useNotionProjects.ts
import { useState, useEffect } from 'react';
import { ProjectData } from '../components/pages/ProjectDetail/projectData';
import { fetchAllProjects, fetchProject, fetchProjectList } from '../services/notionClient';

// グローバルキャッシュ
interface ProjectCache {
  data: ProjectData;
  timestamp: number;
}

const projectCache = new Map<string, ProjectCache>();
let allProjectsCache: { data: Record<string, ProjectData>, timestamp: number } | null = null;

// キャッシュの有効期限（5分）
const CACHE_DURATION = 5 * 60 * 1000;

// キャッシュが有効かチェック
const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

// 全プロジェクト取得 - キャッシュ対応
export const useAllProjects = () => {
  const [projects, setProjects] = useState<Record<string, ProjectData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        // キャッシュチェック
        if (allProjectsCache && isCacheValid(allProjectsCache.timestamp)) {
          console.log('📦 Using cached all projects data');
          setProjects(allProjectsCache.data);
          setLoading(false);
          return;
        }

        console.log('🔄 Fetching fresh all projects data');
        setLoading(true);
        const data = await fetchAllProjects();

        // キャッシュに保存
        allProjectsCache = {
          data,
          timestamp: Date.now()
        };

        // 個別プロジェクトキャッシュも更新
        Object.entries(data).forEach(([id, project]) => {
          projectCache.set(id, {
            data: project,
            timestamp: Date.now()
          });
        });

        setProjects(data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  return { projects, loading, error };
};

// 特定プロジェクト取得 - キャッシュ対応
export const useProject = (projectId: string) => {
  const [project, setProject] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        // キャッシュチェック
        const cached = projectCache.get(projectId);
        if (cached && isCacheValid(cached.timestamp)) {
          console.log(`📦 Using cached project data for ${projectId}`);
          setProject(cached.data);
          setLoading(false);
          return;
        }

        console.log(`🔄 Fetching fresh project data for ${projectId}`);
        setLoading(true);
        const data = await fetchProject(projectId);

        // キャッシュに保存（dataがnullでない場合のみ）
        if (data) {
          projectCache.set(projectId, {
            data,
            timestamp: Date.now()
          });
        }

        setProject(data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  return { project, loading, error };
};

// プロジェクト一覧（軽量版）取得
export const useProjectList = () => {
  const [projects, setProjects] = useState<Array<{
    id: string;
    title: string;
    category: string;
    image: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadProjectList = async () => {
      try {
        setLoading(true);
        const data = await fetchProjectList();
        setProjects(data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectList();
  }, []);

  return { projects, loading, error };
};